<template>
    <div class="fullscreen-popup">
        <button class="close-button" @click="handleClose">
            <b-icon-x-circle scale="1.5" />
        </button>

        <div class="popup-content">
            <!-- Report Generation View -->
            <div v-if="isGenerating" class="report-generation-container">
                <h2 class="generation-title">Generating Report Now...</h2>
                <div class="progress-stats">
                    <div class="progress-percentage">{{ progressPercentage }}%</div>
                    <div class="progress-bar-container">
                        <div class="progress-bar" :style="{ width: progressPercentage + '%' }"></div>
                    </div>
                </div>
            </div>

            <!-- Next Assessment Preview -->
            <div v-else-if="nextAssessmentVideo.id" class="assessment-preview">
                <h2 class="preview-title">Click on the start assessment button to start the next assessment</h2>

                <div class="timer-section">
                    <h3> Next Assessment will start in</h3>
                    <CircularCountdown :initial-time="30" @timer-complete="handleTimerComplete" />
                </div>

                <div class="assessment-card">
                    <h3 class="assessment-title">{{ nextAssessmentVideo.title }}</h3>
                    <div class="video-preview" v-if="nextAssessmentVideo.thumbnail">
                        <youtube class="preview-video" :video-id="videoEmbedId" ref="youtube" :player-vars="playerVars"
                            disablepictureinpicture controlslist="nodownload" muted></youtube>
                    </div>

                    <div class="assessment-info">


                        <b-button class="start-assessment-btn" @click="startNextAssessment" variant="primary">
                            START NEXT ASSESSMENT
                            <b-icon-arrow-right-circle class="ml-2" width="40" height="40" />
                        </b-button>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
import WorkoutComponent from "@/module_assessment/components/AssessmentWorkoutComponent.vue";
import { createNamespacedHelpers, mapState, mapMutations } from "vuex";
import assessmentService from "../api";
import CircularCountdown from '../../module_assessment/components/CountDown.vue'
const {
    mapState: workoutState,
} = createNamespacedHelpers("user/workout");

const {
    mapState: endWorkoutState,
    mapMutations: endWorkoutMutations,
} = createNamespacedHelpers("user/endworkout");

export default {
    name: 'AssessmentPopup',
    components: {
        WorkoutComponent, CircularCountdown
    },
    data() {
        return {
            nextAssessmentVideo: {},
            isGenerating: false,
            userInfo: this.$store.state.userInfo,
            progressPercentage: 0,
            countdownTime: 30,
            countdownInterval: null,
            playerVars: {
                autoplay: 1,
                controls: 1,
                rel: 0,
                start: 0,
                modestbranding: 1,
                showsearch: 0,
                playsinline: 1,
                vq: 'medium'
            },
        };
    },
    mounted() {
        this.previewNextAssessment()
    },
    computed: {
        ...mapState(["loggingIn"]),
        ...endWorkoutState(["workoutData"]),
        ...workoutState(["videoId", "tenantName", "tenantId"]),
        hasRemainingAssessments() {
            const remainingAssessments = this.getRemainingAssessments();
            return remainingAssessments.length > 1;
        },
        showNextAssessmentButton() {
            return this.workoutData?.assessmentId && this.userInfo.corporate?.config?.is_assessment;
        },

        videoEmbedId() {
            if (this.nextAssessmentVideo.youtube_embed_id) {
                return this.nextAssessmentVideo.youtube_embed_id;
            }
            if (this.nextAssessmentVideo.thumbnail) {
                const url = this.nextAssessmentVideo.thumbnail.replace(/\/[^\/]*$/, '');
                const fragments = url.split('/');
                return fragments.length && fragments[fragments.length - 1] || '';
            }
            return '';
        }



    },
    watch: {
        isGenerating(newVal) {
            if (newVal) {
                this.startProgressAnimation();
            }
        },
    },
    methods: {
        ...mapMutations(["setCloseOnHeader"]),
        ...endWorkoutMutations(["setWorkoutData"]),
        handleTimerComplete() {
            this.startNextAssessment()
        },
        startProgressAnimation() {
            const interval = setInterval(() => {
                if (this.progressPercentage < 100) {
                    this.progressPercentage += 1;
                } else {
                    clearInterval(interval);
                }
            }, 300);
        },

        handleClose(){
            this.$router.push("/fsb-assessment")
        },

        startCountdown() {
            this.countdownInterval = setInterval(() => {
                if (this.countdownTime > 0) {
                    this.countdownTime--;
                } else {
                    clearInterval(this.countdownInterval);
                    this.startNextAssessment();
                }
            }, 1000);
        },

        async previewNextAssessment() {
            const remainingVideos = this.getRemainingAssessments();
            const nextVideo = remainingVideos?.[1];
            this.remainingVideosLeft = remainingVideos.slice(1).join(',')
            if (nextVideo) {
                const response = await assessmentService.getNextAssessmentVideoData(nextVideo);
                this.nextAssessmentVideo = response.data;
                this.startCountdown();
            } else {
                this.isGenerating = true;
                await assessmentService.viewAnalysisReport(this.workoutData?.assessmentId)
                this.$nextTick(()=>{
                    this.$router.push(`/fsb-assessment/viewReport/${this.workoutData?.assessmentId}`)
                })
            }
        },

        getRemainingAssessments() {
            return this.workoutData?.remainingVideoIds
                ? this.workoutData.remainingVideoIds.split(",").map(Number)
                : [];
        },

        startNextAssessment() {
            if (this.workoutData.assessmentId && this.nextAssessmentVideo && this.remainingVideosLeft) {
                const url = this.getVideoPath(
                    this.nextAssessmentVideo,
                    this.remainingVideosLeft,
                    this.workoutData.assessmentId
                );
                this.$router.push(url);
            }
        },

        getVideoPath(video, remainingVideoIds, assessmentId) {
            const currentPath = encodeURIComponent(this.$route.fullPath);
            let query = new URLSearchParams();

            query.set('returnToOnCancel', currentPath);
            query.set('returnToOnEnd', 'EndFsbAssessment');
            query.set('id', video.id);
            query.set('remainingVideoIds', remainingVideoIds);
            query.set('assessmentId', assessmentId);
            query.set('isFSB', true);
            query.set('skipIntro', true);

            const workoutTypeId = video.workoutType?.workout_type_id;

            if (workoutTypeId === 4) {
                query.set('duration', video.duration);
                query.set('sets', 1);
                return `/workout/rep?${query.toString()}`;
            } else if (workoutTypeId === 5) {
                query.set('duration', video.duration);
                query.set('sets', 1);
                return `/workout/hold-rep?${query.toString()}`;
            }

            return `/workout/${(video?.Tenant?.name || 'tenantName').replaceAll(/\s/g, "")}?${query.toString()}`;
        },
    },
    beforeDestroy() {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
        }
    },
};
</script>

<style lang="scss" scoped>
.fullscreen-popup {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.95);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.close-button {
    position: absolute;
    top: 20px;
    right: 20px;
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 8px;

    &:hover {
        opacity: 0.8;
    }
}

.popup-content {
    width: 100%;
    padding: 2rem;
    color: white;
}

.report-generation-container {
    text-align: center;
}

.generation-title {
    font-size: 2.5rem;
    font-weight: 600;
    margin-bottom: 2rem;
    color: white;
}

.progress-stats {
    max-width: 600px;
    margin: 0 auto;
}

.progress-percentage {
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 1rem;
}

.progress-bar-container {
    height: 8px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    overflow: hidden;
}

.progress-bar {
    height: 100%;
    background: #73BC44;
    border-radius: 4px;
    transition: width 0.3s ease;
}

.assessment-preview {
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.preview-title {
    font-size: 2rem;
    font-weight: 600;
    margin-bottom: 2rem;
    color: white;
}

.assessment-card {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 16px;
    padding: 1.5rem;
    margin-top: 2rem;
    width: 800px;
}

.preview-video {
    width: 100%;
    height: 100%;
}

::v-deep iframe {
    width: 100%;
    height: 100%;
}

.video-preview {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 1.5rem;
    min-height: 350px;

    .thumbnail {
        width: 100%;
        height: auto;
        object-fit: cover;
    }


    .play-overlay {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(115, 188, 68, 0.9);
        border-radius: 50%;
        padding: 1rem;
    }
}

.assessment-info {
    text-align: left;
}

.assessment-title {
    font-size: 2rem;
    font-weight: 600;
    margin-bottom: 1rem;
}

.assessment-description {
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 1.5rem;
}

.timer-section {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;

    .timer-icon {
        background: #73BC44;
        border-radius: 50%;
        padding: 0.75rem;
    }

    .countdown {
        font-size: 1.25rem;
        font-weight: 500;
    }
}

.start-assessment-btn {
    width: 100%;
    background: #73BC44;
    border: none;
    border-radius: 25px;
    padding: 1rem;
    font-size: 2rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    transition: background-color 0.3s ease;

    &:hover {
        background: #5fa338;
    }
}

.action-button {
    padding: 1rem 2rem;
    background: #73BC44;
    border: none;
    border-radius: 25px;
    font-size: 1.125rem;
    font-weight: 600;
    color: white;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: background-color 0.3s ease;

    &:hover {
        background: #5fa338;
    }
}

@media (max-width: 768px) {
    .popup-content {
        padding: 1rem;
    }

    .generation-title {
        font-size: 1.75rem;
    }

    .preview-title {
        font-size: 1.5rem;
    }

    .assessment-title {
        font-size: 1.25rem;
    }

    .timer-section .countdown {
        font-size: 1rem;
    }

    .start-assessment-btn,
    .action-button {
        font-size: 1rem;
        padding: 0.75rem;
    }
}
</style>